import React, { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { aiAutomationApi } from "../../utils/aiAutomationApi";
import { getMetadata } from "../../utils/encryptedStorage";

const DEFAULT_MAIL_SETTINGS = {
  auto_sync_enabled: true,
  auto_sync_frequency_seconds: 3600,
  followup_enabled: true,
  followup_interval_seconds: 172800,
  max_followups_before_decline: 3,
  manual_followup_cooldown_seconds: 172800,
  nudge_check_interval_seconds: 86400,
  nudge_daily_time: "11:00",
  decline_email: { enabled: true, base_template: "" },
  email_signature: { title: "Best regards,", name: "", position: "", company: "", phone: "", email: "" },
  openai_settings: { model: "gpt-4o-mini", temperature: 0.7, max_tokens: 1000 },
};

const Toggle = ({ checked, onChange }) => (
  <div
    onClick={() => onChange(!checked)}
    className={`relative w-12 h-6 rounded-full cursor-pointer transition-colors ${checked ? "bg-green-500" : "bg-gray-300 dark:bg-gray-600"}`}
    role="switch"
    aria-checked={checked}
  >
    <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-md transition-all duration-200 ${checked ? "left-6" : "left-0.5"}`} />
  </div>
);

const MailSettingsPage = () => {
  const [mailSettings, setMailSettings] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState("");
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);

  const loadMailSettings = useCallback(async () => {
    try {
      setIsLoading(true);
      const metadata = getMetadata();
      const cid = metadata?.cid;
      if (!cid) return;
      const res = await aiAutomationApi.get(`/email/settings/${cid}`);
      if (res?.settings) {
        const loaded = res.settings;
        // Auto-populate company name from business details if signature company is empty
        if (!loaded.email_signature?.company && metadata?.company_name) {
          loaded.email_signature = {
            ...(loaded.email_signature || {}),
            company: metadata.company_name,
          };
        }
        // Auto-populate name from user metadata if empty
        if (!loaded.email_signature?.name && metadata?.name) {
          loaded.email_signature = {
            ...(loaded.email_signature || {}),
            name: metadata.name,
          };
        }
        setMailSettings(loaded);
      } else {
        // No settings exist yet — use defaults with company name from metadata
        const defaults = { ...DEFAULT_MAIL_SETTINGS };
        if (metadata?.company_name) {
          defaults.email_signature = { ...defaults.email_signature, company: metadata.company_name };
        }
        if (metadata?.name) {
          defaults.email_signature = { ...defaults.email_signature, name: metadata.name };
        }
        setMailSettings(defaults);
      }
    } catch (err) {
      console.error("Failed to load mail settings:", err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMailSettings();
  }, [loadMailSettings]);

  const handleChange = (key, value) => {
    setMailSettings((prev) => ({ ...prev, [key]: value }));
  };

  const handleNested = (section, key, value) => {
    setMailSettings((prev) => ({
      ...prev,
      [section]: { ...(prev[section] || {}), [key]: value },
    }));
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setSaveMsg("");
      const metadata = getMetadata();
      const cid = metadata?.cid;
      if (!cid) throw new Error("No company ID found");
      await aiAutomationApi.post("/email/settings", { cid: String(cid), settings: mailSettings });
      setSaveMsg("Settings saved successfully!");
    } catch (err) {
      console.error("Failed to save mail settings:", err);
      setSaveMsg("Failed to save settings");
    } finally {
      setIsSaving(false);
      setTimeout(() => setSaveMsg(""), 3000);
    }
  };

  const handleResetDefaults = async () => {
    setResetConfirmOpen(false);
    try {
      setIsSaving(true);
      setSaveMsg("");
      const metadata = getMetadata();
      const cid = metadata?.cid;
      if (!cid) throw new Error("No company ID found");
      await aiAutomationApi.post("/email/settings", { cid: String(cid), settings: DEFAULT_MAIL_SETTINGS });
      setMailSettings({ ...DEFAULT_MAIL_SETTINGS });
      setSaveMsg("Settings reset to defaults!");
    } catch (err) {
      console.error("Failed to reset mail settings:", err);
      setSaveMsg("Failed to reset settings");
    } finally {
      setIsSaving(false);
      setTimeout(() => setSaveMsg(""), 3000);
    }
  };

  return (
    <div className="min-h-screen w-full flex flex-col bg-[#F6F7F9] dark:bg-gray-900">
      <main className="flex-1 bg-white dark:bg-gray-900 p-6 sm:p-10 transition-colors duration-250">
        {/* Page Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-xl font-semibold text-slate-800 dark:text-white">Mail Settings</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Configure email automation preferences</p>
          </div>
          <div className="flex items-center gap-3">
            {saveMsg && (
              <span className={`text-sm font-medium ${saveMsg.includes("success") || saveMsg.includes("reset") ? "text-green-600" : "text-red-600"}`}>
                {saveMsg}
              </span>
            )}
            <button
              onClick={() => setResetConfirmOpen(true)}
              disabled={isSaving || !mailSettings}
              className="px-4 py-2 text-sm font-medium text-red-600 border border-red-300 dark:border-red-700 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50 transition-colors"
            >
              Reset to Defaults
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving || !mailSettings}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg disabled:opacity-50 transition-colors"
            >
              {isSaving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-300 border-t-blue-600"></div>
            <span className="ml-3 text-gray-500 dark:text-gray-400">Loading mail settings...</span>
          </div>
        ) : mailSettings ? (
          <div className="space-y-6 max-w-4xl">
            {/* Auto Sync */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Auto Sync</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">Configure automatic email synchronization timing</p>
              </div>
              <div className="p-6 space-y-5">
                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Enable Auto Sync</label>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Automatically sync emails at the configured interval</p>
                  </div>
                  <Toggle checked={!!mailSettings.auto_sync_enabled} onChange={(v) => handleChange("auto_sync_enabled", v)} />
                </div>
                {mailSettings.auto_sync_enabled && (
                  <div className="max-w-xs">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Sync Frequency (minutes)</label>
                    <input
                      type="number"
                      min="1"
                      value={Math.round((mailSettings.auto_sync_frequency_seconds || 3600) / 60)}
                      onChange={(e) => handleChange("auto_sync_frequency_seconds", Math.max(30, parseInt(e.target.value || "1") * 60))}
                      className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="mt-1 text-xs text-gray-400">Minimum: 1 minute</p>
                  </div>
                )}
              </div>
            </div>

            {/* Follow-up / Nudge */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Nudge Replies & Follow-ups</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">Configure automatic follow-up and nudge behavior</p>
              </div>
              <div className="p-6 space-y-5">
                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Enable Follow-ups</label>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Automatically send follow-up emails to non-responsive candidates</p>
                  </div>
                  <Toggle checked={!!mailSettings.followup_enabled} onChange={(v) => handleChange("followup_enabled", v)} />
                </div>
                {mailSettings.followup_enabled && (
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Follow-up Interval (hours)</label>
                      <input
                        type="number"
                        min="1"
                        value={Math.round((mailSettings.followup_interval_seconds || 172800) / 3600)}
                        onChange={(e) => handleChange("followup_interval_seconds", Math.max(3600, parseInt(e.target.value || "1") * 3600))}
                        className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <p className="mt-1 text-xs text-gray-400">Hours between automatic follow-ups</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Max Follow-ups Before Decline</label>
                      <input
                        type="number"
                        min="1"
                        max="10"
                        value={mailSettings.max_followups_before_decline || 3}
                        onChange={(e) => handleChange("max_followups_before_decline", Math.max(1, parseInt(e.target.value || "3")))}
                        className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <p className="mt-1 text-xs text-gray-400">After this many nudges, send decline email</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Manual Follow-up Cooldown (hours)</label>
                      <input
                        type="number"
                        min="1"
                        value={Math.round((mailSettings.manual_followup_cooldown_seconds || 172800) / 3600)}
                        onChange={(e) => handleChange("manual_followup_cooldown_seconds", Math.max(3600, parseInt(e.target.value || "1") * 3600))}
                        className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <p className="mt-1 text-xs text-gray-400">Minimum time between manual follow-ups</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nudge Daily Time</label>
                      <input
                        type="time"
                        value={mailSettings.nudge_daily_time || "11:00"}
                        onChange={(e) => handleChange("nudge_daily_time", e.target.value)}
                        className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <p className="mt-1 text-xs text-gray-400">Time of day to send nudge emails</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Email Signature */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Email Signature</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">Configure the email signature used in outgoing emails. Company name is required.</p>
              </div>
              <div className="p-6">
                <div className="grid sm:grid-cols-2 gap-4">
                  {[
                    { key: "title", label: "Signature Title", placeholder: "Best regards,", removable: true },
                    { key: "name", label: "Name", placeholder: "John Doe", removable: true },
                    { key: "position", label: "Position", placeholder: "HR Manager", removable: true },
                    { key: "company", label: "Company", placeholder: "Acme Corp", removable: false, required: true },
                    { key: "phone", label: "Phone", placeholder: "+1 234 567 890", removable: true },
                    { key: "email", label: "Email", placeholder: "john@acme.com", type: "email", removable: true },
                  ].map(({ key, label, placeholder, type, removable, required }) => (
                    <div key={key}>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {label} {required && <span className="text-red-500">*</span>}
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type={type || "text"}
                          value={mailSettings.email_signature?.[key] || ""}
                          onChange={(e) => handleNested("email_signature", key, e.target.value)}
                          placeholder={placeholder}
                          className={`flex-1 px-3 py-2 rounded-lg border ${
                            required && !(mailSettings.email_signature?.[key] || "").trim()
                              ? "border-red-400 dark:border-red-500"
                              : "border-gray-300 dark:border-gray-600"
                          } bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500`}
                        />
                        {removable && (mailSettings.email_signature?.[key] || "").trim() && (
                          <button
                            type="button"
                            onClick={() => handleNested("email_signature", key, "")}
                            className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                            title={`Clear ${label}`}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        )}
                      </div>
                      {required && !(mailSettings.email_signature?.[key] || "").trim() && (
                        <p className="mt-1 text-xs text-red-500">This field is required and cannot be removed</p>
                      )}
                    </div>
                  ))}
                </div>

                {/* Live Signature Preview */}
                <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Signature Preview</h4>
                  <div className="rounded-lg bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 p-4 text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                    {(() => {
                      const sig = mailSettings.email_signature || {};
                      const title = (sig.title || "").trim();
                      const name = (sig.name || "").trim();
                      const position = (sig.position || "").trim();
                      const company = (sig.company || "").trim();
                      const phone = (sig.phone || "").trim();
                      const email = (sig.email || "").trim();
                      const hasContent = title || name || position || company || phone || email;
                      if (!hasContent) {
                        return <span className="text-gray-400 dark:text-gray-500 italic">No signature configured yet</span>;
                      }
                      return (
                        <>
                          {title && <div>{title}</div>}
                          {name && <div className="font-semibold">{name}</div>}
                          {position && <div>{position}</div>}
                          {company && <div>{company}</div>}
                          {phone && <div>Phone: {phone}</div>}
                          {email && <div>Email: {email}</div>}
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>
            </div>

            {/* Decline Email */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Decline Email</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">Configure the decline email sent after max follow-ups</p>
              </div>
              <div className="p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Enable Decline Emails</label>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Send decline email after max follow-ups are exhausted</p>
                  </div>
                  <Toggle checked={mailSettings.decline_email?.enabled ?? true} onChange={(v) => handleNested("decline_email", "enabled", v)} />
                </div>
                {(mailSettings.decline_email?.enabled ?? true) && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Decline Template</label>
                    <textarea
                      rows={4}
                      value={mailSettings.decline_email?.base_template || ""}
                      onChange={(e) => handleNested("decline_email", "base_template", e.target.value)}
                      placeholder="Thank you for your time..."
                      className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* AI / OpenAI Settings */}
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">AI Settings</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">Configure OpenAI model parameters for email generation</p>
              </div>
              <div className="p-6">
                <div className="grid sm:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Model</label>
                    <select
                      value={mailSettings.openai_settings?.model || "gpt-4o-mini"}
                      onChange={(e) => handleNested("openai_settings", "model", e.target.value)}
                      className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="gpt-4o-mini">GPT-4o Mini</option>
                      <option value="gpt-4o">GPT-4o</option>
                      <option value="gpt-4-turbo">GPT-4 Turbo</option>
                      <option value="gpt-3.5-turbo">GPT-3.5 Turbo</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Temperature</label>
                    <input
                      type="number"
                      min="0"
                      max="2"
                      step="0.1"
                      value={mailSettings.openai_settings?.temperature ?? 0.7}
                      onChange={(e) => handleNested("openai_settings", "temperature", parseFloat(e.target.value || "0.7"))}
                      className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="mt-1 text-xs text-gray-400">0 = deterministic, 2 = creative</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Max Tokens</label>
                    <input
                      type="number"
                      min="100"
                      max="4000"
                      step="100"
                      value={mailSettings.openai_settings?.max_tokens || 1000}
                      onChange={(e) => handleNested("openai_settings", "max_tokens", parseInt(e.target.value || "1000"))}
                      className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            <p>Unable to load mail settings. Please try again.</p>
            <button onClick={loadMailSettings} className="mt-4 px-4 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
              Retry
            </button>
          </div>
        )}

        {/* Reset Defaults Confirmation Modal */}
        {resetConfirmOpen && createPortal(
          <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md mx-4 p-6 space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Reset to Defaults</h3>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Are you sure you want to reset all mail settings to their default values? This action will save immediately and cannot be undone.
              </p>
              <div className="flex justify-end gap-3 pt-2">
                <button
                  onClick={() => setResetConfirmOpen(false)}
                  className="px-4 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleResetDefaults}
                  className="px-4 py-2 text-sm rounded-lg text-white bg-red-600 hover:bg-red-700 font-medium transition-colors"
                >
                  Reset to Defaults
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
      </main>
    </div>
  );
};

export default MailSettingsPage;
